/* eslint-disable no-nested-ternary */
import React, { useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  Grid,
  Box,
  Divider,
} from '@mui/material';
import { formatCurrency } from 'renderer/shared/formatters/formatters';
import { cartItemKey } from 'renderer/features/payments-page/cart/cartItemKey';
import type { SerialRowMap } from 'renderer/features/payments-page/cart/cartTypes';
import { isUpgradeItem } from 'renderer/features/payments-page/cart/cartLineKinds';
import {
  getBasePrice,
  getDiscountedPrice,
  getEffectivePrice,
} from 'renderer/features/payments-page/cart/cartPricing';
import {
  computeCartTotals,
  getLineDisplayTotal,
  isOrderScopeDiscount,
} from 'renderer/features/payments-page/cart/cartTotals';

const baseUrl = `${process.env.SERVER_URL ?? 'http://localhost:3000'}/api`;

interface OrderSummaryProps {
  cartData: any;
}

const isOnSale = (product: any, hostApplication?: any): boolean => {
  const base = getBasePrice(product, hostApplication);
  const effective = getEffectivePrice(product, hostApplication);
  const until = product?.sale?.until
    ? new Date(product.sale.until).getTime()
    : 0;

  return effective < base && until > Date.now();
};

const getUpgradeHostAppLabel = (item: any): string => {
  const rows = item?.serialMap || [];
  if (!Array.isArray(rows) || rows.length === 0) return '—';

  const names = rows
    .map((r: any) => (r.targetHostApp || r.hostApp)?.displayName)
    .filter(Boolean) as string[];

  if (names.length === 0) return '—';

  const unique = Array.from(new Set(names));

  if (unique.length <= 2) return unique.join(', ');
  return `${unique.slice(0, 2).join(', ')} + ${unique.length - 2} more`;
};

/** Same unit rules as CartLinePriceDisplay (order-scope discounts never
 * change the per-line unit — they apply once to the order total). */
const getLineUnitDisplay = (
  item: any,
): { unit: number; strike: number | null } => {
  const base = getBasePrice(item.productId, item.hostApplication);
  const saleAware = getEffectivePrice(item.productId, item.hostApplication);
  const applyLineDiscount =
    !!item.discountId && !isOrderScopeDiscount(item.discountId);

  const unit = applyLineDiscount
    ? getDiscountedPrice(item.productId, item.discountId, item.hostApplication)
    : saleAware;

  return { unit, strike: unit < base ? base : null };
};

const OrderSummary: React.FC<OrderSummaryProps> = ({ cartData }) => {
  const items = useMemo(() => cartData?.items || [], [cartData?.items]);

  const serialRowsByItem: SerialRowMap = useMemo(() => {
    const map: SerialRowMap = {};
    items.forEach((it: any) => {
      map[cartItemKey(it)] = it.serialMap || [];
    });
    return map;
  }, [items]);

  const {
    baseTotal,
    totalDiscount,
    grandTotal,
    // discountDescriptors — cart-style breakdown; hide on Purchase for now
  } = useMemo(
    () => computeCartTotals(items, serialRowsByItem),
    [items, serialRowsByItem],
  );

  return (
    <Card>
      <CardContent>
        <List>
          {items.map((item: any) => {
            const image = item.productId.productImageId
              ? `${baseUrl}/product-images/${item.productId.productImageId}`
              : '/images/placeholder.png';

            const isUpgrade = isUpgradeItem(item);
            const quantity = isUpgrade
              ? item.serialMap?.length || item.quantity
              : item.quantity;

            const saleActive =
              !isUpgrade && isOnSale(item.productId, item.hostApplication);

            const { unit: unitPrice, strike: strikePrice } = !isUpgrade
              ? getLineUnitDisplay(item)
              : { unit: 0, strike: null };

            const lineTotal = getLineDisplayTotal(item, serialRowsByItem);

            return (
              <ListItem
                key={item._id}
                style={{ paddingLeft: 0 }}
                alignItems="flex-start"
              >
                <Box sx={{ position: 'relative', mr: 2 }}>
                  {saleActive && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        maxWidth: 140,
                        backgroundColor: 'red',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '12px',
                        textAlign: 'center',
                        py: 0.5,
                        zIndex: 2,
                      }}
                    >
                      SALE
                    </Box>
                  )}

                  <Box
                    sx={{
                      width: 140,
                      height: 140,
                      border: '1px solid #ccc',
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={image}
                      alt={item.productId.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  </Box>
                </Box>

                <Box>
                  <Typography variant="subtitle1">
                    {item.productId.name}
                  </Typography>

                  <Typography variant="body2">
                    {item.platform},{' '}
                    {isUpgrade
                      ? getUpgradeHostAppLabel(item)
                      : item.hostApplication?.displayName || '—'}
                  </Typography>

                  <Typography variant="body2">x {quantity}</Typography>

                  {!isUpgrade && (
                    <Typography variant="body2" sx={{ color: '#1E88E5' }}>
                      {strikePrice != null ? (
                        <>
                          <span
                            style={{
                              textDecoration: 'line-through',
                              opacity: 0.6,
                              marginRight: 6,
                            }}
                          >
                            {formatCurrency(strikePrice)}
                          </span>
                          {formatCurrency(unitPrice)}
                        </>
                      ) : (
                        formatCurrency(unitPrice)
                      )}
                    </Typography>
                  )}

                  <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', opacity: 0.7 }}
                  >
                    Total: {formatCurrency(lineTotal)}
                  </Typography>
                </Box>
              </ListItem>
            );
          })}
        </List>

        <Box mt={2}>
          <Grid container>
            <Grid item xs={6}>
              <Typography>Subtotal</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography align="right">{formatCurrency(baseTotal)}</Typography>
            </Grid>

            {totalDiscount > 0 && (
              <>
                <Grid item xs={6}>
                  <Typography>Discount</Typography>
                  {/* Cart-style per-line breakdown — commented out on Purchase
                  {discountDescriptors.length > 0 && (
                    <Box sx={{ mt: 0.5 }}>
                      {discountDescriptors.map((d) => (
                        <Typography
                          key={d.key}
                          variant="caption"
                          display="block"
                          sx={{ opacity: 0.85, lineHeight: 1.3 }}
                        >
                          {d.text}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  */}
                </Grid>
                <Grid item xs={6}>
                  <Typography align="right" color="green">
                    -{formatCurrency(totalDiscount)}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>

          <Divider sx={{ my: 1 }} />

          <Grid container>
            <Grid item xs={6}>
              <Typography variant="subtitle1">Total</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" align="right">
                {formatCurrency(grandTotal)}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default OrderSummary;
